import React, { useRef, useState } from "react";
import { Film, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAttachments } from "@/lib/useUploader";

const FPS = [23.976, 24, 25, 29.97, 30];

// JPEG / PNG image sequence -> one frame-exact video that is used as the source footage.
export default function SequenceImport({ projectId, onUploaded, onReady, onError, slot, testid = "cp-seq" }) {
  const input = useRef(null);
  const [fps, setFps] = useState(24);
  const [busy, setBusy] = useState("");
  const up = useAttachments(projectId, 2000, onUploaded, false, slot);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => /image\/(png|jpeg)/.test(f.type));
    e.target.value = "";
    if (files.length < 2) { if (files.length) onError?.("Pick at least 2 PNG / JPEG frames"); return; }
    if (files.length / fps > 30.05) { onError?.(`${files.length} frames at ${fps} fps is ${(files.length / fps).toFixed(1)} s — max 30 s`); return; }
    setBusy(`Uploading ${files.length} frames…`);
    try {
      up.setRaw(() => []);
      await up.add(files);
      let ids = [];
      for (let i = 0; i < 1800; i += 1) {
        let cur = [];
        up.setRaw((c) => { cur = c; return c; });
        ids = cur.filter((x) => x.id).map((x) => x.id);
        if (!cur.some((x) => x.uploading) && cur.length) break;
        await new Promise((r) => setTimeout(r, 500));
      }
      if (ids.length < 2) throw new Error("Upload failed");
      setBusy("Building the clip…");
      const { data } = await api.post("/bfx/sequence/assemble", { project_id: projectId, ref_ids: ids, fps, name: files[0].name.replace(/[_.-]?\d+\.\w+$/, "") || "sequence" });
      up.setRaw(() => []);
      onReady(data);
      toast.success(`Image sequence imported — ${ids.length} frames @ ${fps} fps`);
    } catch (err) { onError?.(errMsg(err, "Could not import that image sequence")); } finally { setBusy(""); }
  };
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <input ref={input} type="file" multiple accept="image/png,image/jpeg" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <button type="button" disabled={!!busy} onClick={() => input.current?.click()} data-testid={`${testid}-button`}
        className="h-8 px-3 rounded-lg border border-[#1477d2]/60 text-xs text-white flex items-center gap-1.5 hover:bg-[#0747a1]/30 disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />}{busy || "Import image sequence"}</button>
      <select value={fps} onChange={(e) => setFps(Number(e.target.value))} disabled={!!busy} data-testid={`${testid}-fps`} className="ax-input bfx-input !h-8 !py-0 text-xs w-24">
        {FPS.map((f) => <option key={f} value={f}>{f} fps</option>)}
      </select>
    </div>
  );
}
